/**
 * Document Types Management Page
 * Admin page for managing configurable document types
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import useAsyncData from '../../hooks/useAsyncData';
import { useNotification } from '../../contexts/NotificationContext';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const DOC_TYPE_COLUMNS = [
  { key: 'code',             label: 'Code',              align: 'left',   getValue: (d) => d.code },
  { key: 'display_name',     label: 'Display Name',      align: 'left',   getValue: (d) => d.display_name },
  { key: 'description',      label: 'Description',       align: 'left',   getValue: (d) => d.description },
  { key: 'validity_months',  label: 'Validity (Months)', align: 'center', getValue: (d) => d.validity_months },
  { key: 'sort_order',       label: 'Sort Order',        align: 'center', getValue: (d) => d.sort_order },
  { key: 'is_active',        label: 'Status',            align: 'center', getValue: (d) => d.is_active ? 1 : 0 },
  { key: 'is_system',        label: 'Type',              align: 'center', getValue: (d) => d.is_system ? 'System' : 'Custom' },
];

function DocumentTypesPage() {
  const notify = useNotification();
  const sort = useTableSort();
  const { data: documentTypes, loading, error: loadError, reload: reloadDocumentTypes } = useAsyncData(
    () => apiGet('/refdata/document-types'),
    { defaultValue: [] }
  );

  // Surface load failures as a toast
  useEffect(() => {
    if (loadError) notify.error(loadError);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify is stable
  }, [loadError]);

  // Edit/Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [formData, setFormData] = useState({
    code: '',
    display_name: '',
    description: '',
    validity_months: '',
    sort_order: 50,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleOpenCreate = () => {
    setFormData({
      code: '',
      display_name: '',
      description: '',
      validity_months: '',
      sort_order: 50,
      is_active: true,
    });
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleOpenEdit = (docType) => {
    setFormData({
      id: docType.id,
      code: docType.code,
      display_name: docType.display_name,
      description: docType.description || '',
      validity_months: docType.validity_months || '',
      sort_order: docType.sort_order || 50,
      is_active: docType.is_active,
      is_system: docType.is_system,
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.display_name) {
      notify.error('Code and display name are required');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        code: formData.code,
        display_name: formData.display_name,
        description: formData.description || null,
        validity_months: formData.validity_months ? parseInt(formData.validity_months) : null,
        sort_order: parseInt(formData.sort_order) || 50,
        is_active: formData.is_active,
      };

      if (dialogMode === 'create') {
        await apiPost('/refdata/document-types', payload);
        notify.success('Document type created successfully');
      } else {
        await apiPut(`/refdata/document-types/${formData.id}`, payload);
        notify.success('Document type updated successfully');
      }

      setDialogOpen(false);
      reloadDocumentTypes();
    } catch (err) {
      notify.error('Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (docType) => {
    try {
      await apiPost(`/refdata/document-types/${docType.id}/toggle-active`);
      notify.success(`Document type ${docType.is_active ? 'deactivated' : 'activated'} successfully`);
      reloadDocumentTypes();
    } catch (err) {
      notify.error('Failed to toggle status: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeleteClick = (docType) => {
    setDeleteTarget(docType);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await apiDelete(`/refdata/document-types/${deleteTarget.id}`);
      notify.success('Document type deleted successfully');
      setDeleteDialog(false);
      setDeleteTarget(null);
      reloadDocumentTypes();
    } catch (err) {
      notify.error('Failed to delete: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Document Types</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={reloadDocumentTypes}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Type
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage document types that can be uploaded. System types cannot be deleted but can be deactivated.
      </Typography>
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : documentTypes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No document types configured
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ mt: 2 }}
            >
              Add First Type
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {DOC_TYPE_COLUMNS.map((col) => (
                    <SortableHeaderCell key={col.key} sort={sort} columnKey={col.key} align={col.align}>
                      {col.label}
                    </SortableHeaderCell>
                  ))}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sort.sortRows(documentTypes, DOC_TYPE_COLUMNS).map((docType) => (
                  <TableRow key={docType.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {docType.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {docType.display_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {docType.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {docType.validity_months || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {docType.sort_order}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={docType.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={docType.is_active ? 'success' : 'default'}
                        onClick={() => handleToggleActive(docType)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={docType.is_system ? 'System' : 'Custom'}
                        size="small"
                        variant="outlined"
                        color={docType.is_system ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(docType)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!docType.is_system && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(docType)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add Document Type' : 'Edit Document Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
              required
              disabled={dialogMode === 'edit'}
              helperText="Unique identifier (lowercase, underscores only)"
              placeholder="e.g., bank_statement"
            />

            <TextField
              label="Display Name"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              required
              placeholder="e.g., Bank Statement"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              placeholder="Optional description of this document type"
            />

            <TextField
              label="Validity (Months)"
              type="number"
              value={formData.validity_months}
              onChange={(e) => setFormData({ ...formData, validity_months: e.target.value })}
              helperText="How long documents of this type remain valid (leave empty for no expiry)"
              placeholder="e.g., 12"
            />

            <TextField
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              helperText="Lower numbers appear first in dropdowns"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active (visible in upload forms)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.code || !formData.display_name}
          >
            {saving ? <CircularProgress size={20} /> : dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Document Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.display_name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. If documents of this type exist, deletion will be blocked.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DocumentTypesPage;
